// frontend/src/modules/email/pages/ProviderTab.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { VISUAL } from '../../../design-system/visual';
import { emailApi } from '../api';
import type { EmailProvider, EmailProviderConfig, EmailProviderConfigInput } from '../types';

const DEFAULT: EmailProviderConfigInput = {
  provider: 'platform_fallback', from_email: '', from_name: '', reply_to_email: '',
  smtp_host: '', smtp_port: 587, smtp_secure: false, smtp_username: '', smtp_password: '',
};

export function ProviderTab() {
  const [loaded, setLoaded] = React.useState<EmailProviderConfig | null>(null);
  const [form, setForm] = React.useState<EmailProviderConfigInput>(DEFAULT);
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    emailApi.getProviderConfig()
      .then((cfg) => {
        setLoaded(cfg);
        if (cfg) {
          setForm({
            provider: cfg.provider,
            from_email: cfg.from_email,
            from_name: cfg.from_name ?? '',
            reply_to_email: cfg.reply_to_email ?? '',
            smtp_host: cfg.smtp_host ?? '',
            smtp_port: cfg.smtp_port ?? 587,
            smtp_secure: cfg.smtp_secure ?? false,
            smtp_username: cfg.smtp_username ?? '',
            smtp_password: '',                                   // never returned
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.from_email?.trim()) { toast.error('From email is required'); return; }
    setSaving(true);
    try {
      const cfg = await emailApi.putProviderConfig(form);
      setLoaded(cfg);
      setForm((f) => ({ ...f, smtp_password: '' })); // clear plaintext from state after save
      toast.success('Provider configuration saved');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  function setProvider(v: EmailProvider) {
    setForm((f) => ({ ...f, provider: v }));
  }

  if (loading) return <LoadingState variant="inline" />;

  return (
    <form onSubmit={save} className="max-w-3xl space-y-6">
      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Provider</h3>
        <div className="grid gap-1.5">
          <Label>Send-from provider</Label>
          <Select value={form.provider} onValueChange={(v) => setProvider(v as EmailProvider)}>
            <SelectTrigger className="max-w-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="platform_fallback">Platform fallback (zero setup)</SelectItem>
              <SelectItem value="custom_smtp">Custom SMTP</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[12px] text-muted-foreground mt-1">
            {form.provider === 'platform_fallback'
              ? 'Sends via the Worqly mail relay. Replies route back to your billing email.'
              : 'Connect your own SMTP server (Mailgun, SES, Postmark, Gmail App-Password, etc.).'}
          </p>
        </div>
      </section>

      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Identity</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label>From email<span className="text-destructive ml-0.5">*</span></Label>
            <Input value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} type="email" placeholder="noreply@yourcompany.com" required />
          </div>
          <div className="grid gap-1.5">
            <Label>From name</Label>
            <Input value={form.from_name ?? ''} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="Your Company" />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Reply-to email</Label>
            <Input value={form.reply_to_email ?? ''} onChange={(e) => setForm({ ...form, reply_to_email: e.target.value })} type="email" placeholder="hello@yourcompany.com" />
          </div>
        </div>
      </section>

      {form.provider === 'custom_smtp' && (
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>SMTP credentials</h3>
          <div className="grid gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="grid gap-1.5 sm:col-span-2">
                <Label>Host</Label>
                <Input value={form.smtp_host ?? ''} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.example.com" />
              </div>
              <div className="grid gap-1.5">
                <Label>Port</Label>
                <Input type="number" value={form.smtp_port ?? 587} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} />
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.smtp_secure ?? false}
                onChange={(e) => setForm({ ...form, smtp_secure: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-border accent-saffron"
              />
              <span className="flex-1">
                <span className="text-[13.5px] font-semibold text-foreground block">Use implicit TLS (port 465)</span>
                <span className="text-[12.5px] text-muted-foreground block mt-0.5">Leave unchecked for STARTTLS on port 587.</span>
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Username</Label>
                <Input value={form.smtp_username ?? ''} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label>Password{loaded?.smtp_password_set && <span className="text-[11px] text-muted-foreground ml-2 font-normal">(leave blank to keep existing)</span>}</Label>
                <Input type="password" value={form.smtp_password ?? ''} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} />
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
